/**
 * Bank Transaction Service - Frontend service for bank transaction operations
 */

import api from "@/shared/api";

/**
 * Import bank transactions
 * @param {Array} transactions - Array of transaction objects
 * @returns {Promise<Object>} Import result
 */
export const importBankStatementPdf = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/bank-transactions/statements/import", formData);
    return {
      success: response.success !== false,
      data: response.statement || response.data || response,
      imported: response.imported || 0,
      skipped: Boolean(response.skipped),
      warnings: response.warnings || [],
      error: response.error,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

export const getBankStatements = async () => {
  try {
    const response = await api.get("/bank-transactions/statements");
    return {
      success: true,
      data: response.statements || response.data || [],
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
};

export const importBankTransactions = async (transactions) => {
  try {
    const response = await api.post("/bank-transactions/import", { transactions });
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};

/**
 * Get bank transactions with filters
 * @param {Object} filters - Filter criteria
 * @returns {Promise<Object>} Bank transactions
 */
export const getBankTransactions = async (filters = {}) => {
  try {
    const response = await api.get("/bank-transactions", { params: filters });
    return {
      success: true,
      data: response.transactions || response.data || [],
      total: response.total || 0,
      limit: response.limit || 100,
      offset: response.offset || 0,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
      total: 0,
    };
  }
};

/**
 * Get bank transaction by ID
 * @param {string} id - Transaction ID
 * @returns {Promise<Object>} Bank transaction
 */
export const searchBankMatchCandidates = async ({
  query,
  amount,
  occurredAt,
} = {}) => {
  try {
    const response = await api.get("/bank-transactions/candidates", {
      params: {
        q: query,
        amount,
        occurredAt,
      },
    });
    return {
      success: response.success !== false,
      data: response.candidates || response.data || [],
      error: response.error,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: [],
    };
  }
};

export const getBankTransaction = async (id) => {
  try {
    const response = await api.get(`/bank-transactions/${id}`);
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Match bank transaction to invoice/quote/job
 * @param {string} id - Transaction ID
 * @param {Object} matchData - Match data
 * @returns {Promise<Object>} Match result
 */
export const matchBankTransaction = async (id, matchData) => {
  try {
    const response = await api.post(`/bank-transactions/${id}/match`, matchData);
    return {
      success: true,
      data: response.data || response,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || error.message,
    };
  }
};
